import { RangeEntry, RangeEntryWrapper } from '../../Types'
import { sortRangeEntryWrappers } from '../Sorting/Sorting'
import { processDisplayValue } from '../Utils/Utilities'

/**
 * Function to combine overlapping or sequential ranges.
 * Accounting for the fact that some ranges are only a single number or a string.
 *
 * @param ranges {@type RangeEntryWrapper[]} - The ranges to combine
 */
export const combineOverlappingOrSequentialRanges = (ranges: RangeEntryWrapper[]): RangeEntryWrapper[] =>
    ranges
        .sort(sortRangeEntryWrappers)
        .reduce((accum: RangeEntryWrapper[], next: RangeEntryWrapper): RangeEntryWrapper[] => {
            const { included, isRange, value }: RangeEntryWrapper = next

            // Is the current value a range?
            if (isRange) {
                // Does previous range exist?
                if (accum.length > 0) {
                    // Get the last accumulated value.
                    const previouslyAccumulatedValue: RangeEntryWrapper = accum[accum.length - 1]

                    if (
                        previouslyAccumulatedValue &&
                        previouslyAccumulatedValue.isRange &&
                        included === previouslyAccumulatedValue.included
                    ) {
                        // Get the min and max values from the range.
                        const rangeMin: number = value.min ?? NaN
                        const rangeMax: number = value.max ?? NaN

                        // Get the min and max values from the previously accumulated range.
                        const previouslyAccumulatedMin: number = previouslyAccumulatedValue.value.min
                        const previouslyAccumulatedMax: number = previouslyAccumulatedValue.value.max

                        // Can the current range be combined with the previously accumulated range?
                        if (
                            previouslyAccumulatedMin <= rangeMin + 1 &&
                            previouslyAccumulatedMax >= rangeMin - 1 &&
                            processDisplayValue(previouslyAccumulatedValue.value.minDisplayValue, rangeMin) ===
                                value.minDisplayValue
                        ) {
                            // Combine the ranges.
                            const calculatedMinValue: number = Math.min(previouslyAccumulatedMin, rangeMin)
                            const newMinRangeEntry: RangeEntry = (
                                value.min === calculatedMinValue ? next : previouslyAccumulatedValue
                            ) as RangeEntry

                            const calculatedMaxValue: number = Math.max(previouslyAccumulatedMax, rangeMax)
                            const newMaxRangeEntry: RangeEntry = (
                                value.max === calculatedMaxValue ? next : previouslyAccumulatedValue
                            ) as RangeEntry

                            previouslyAccumulatedValue.value.min = Math.min(rangeMin, previouslyAccumulatedMin)
                            previouslyAccumulatedValue.value.minDisplayValue = newMinRangeEntry.value.minDisplayValue
                            previouslyAccumulatedValue.value.max = Math.max(rangeMax, previouslyAccumulatedMax)
                            previouslyAccumulatedValue.value.maxDisplayValue = newMaxRangeEntry.value.maxDisplayValue

                            return accum
                        }
                    }
                }
            }

            accum.push(next)

            return accum
        }, [] as RangeEntryWrapper[])
